import { configuredFetchBaseQuery } from '@config/rtkQuery';
import { removeEmpty } from '@features/removeEmpty';
import { createApi } from '@reduxjs/toolkit/query/react';

export const userContactsApi = createApi({
  reducerPath: 'userContactsApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    getList: builder.query({
      query: () => ({ url: 'customer/contacts' }),
      providesTags: ['Contacts'],
      transformResponse: (response) =>
        response.map((item) => ({
          id: item.id,
          level: item.level,
          name: item.name,
          email: item.email,
          contactType: item.type.id,
          mailSubscription: item.mail_subscription.map(({ id }) => id),
        })),
    }),
    getDetails: builder.query({
      query: (arg) => {
        const { id } = arg;
        return { url: `customer/contacts/${id}` };
      },
      providesTags: (result, error, { id }) => [{ type: 'ContactDetails', id }],
      transformResponse: (response) => ({
        id: response.id,
        level: response.level,
        name: response.name,
        mobilePhone: response.mobile_phone,
        fixedPhone: response.fixed_phone,
        email: response.email,
        contactType: response.type.id,
        mailSubscription: response.mail_subscription.map(({ id }) => id),
      }),
    }),
    createContact: builder.mutation({
      query(arg) {
        const { data } = arg;
        return {
          url: `customer/contacts`,
          method: 'POST',
          body: {
            level: data.level,
            reg_type: data.contactType,
            name: data.name,
            mobile_phone: data.mobilePhone,
            fixed_phone: data.fixedPhone,
            email: data.email,
            mail_subscription: data.mailSubscription,
          },
        };
      },
      transformResponse: (response) => response.message,
      transformErrorResponse: (response) => {
        if (!response.data) {
          return response;
        }
        const { reg_type: contactType, email, level } = response.data;
        const errors = removeEmpty({ level, email, contactType });
        Object.entries(errors).forEach(([key, value]) => {
          errors[key] = value.join(' ');
        });
        return errors;
      },
      invalidatesTags: (result, error) => (error ? [] : ['Contacts']),
    }),
    deleteContact: builder.mutation({
      query(arg) {
        const { id } = arg;
        return {
          url: `customer/contacts/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error) => (error ? [] : ['Contacts']),
    }),
    updateContact: builder.mutation({
      query(arg) {
        const { id, data } = arg;
        return {
          url: `customer/contacts/${id}`,
          method: 'PUT',
          body: {
            level: data.level,
            reg_type: data.contactType,
            name: data.name,
            mobile_phone: data.mobilePhone,
            fixed_phone: data.fixedPhone,
            email: data.email,
            mail_subscription: data.mailSubscription,
          },
        };
      },
      invalidatesTags: (result, error, { id }) =>
        error ? [] : [{ type: 'ContactDetails', id }, 'Contacts'],
      transformErrorResponse: (response) => {
        if (!response.data) {
          return response;
        }
        const { reg_type: contactType, email, level } = response.data;
        const errors = removeEmpty({ level, email, contactType });
        Object.entries(errors).forEach(([key, value]) => {
          errors[key] = value.join(' ');
        });
        return errors;
      },
    }),
    getMailSubscriptions: builder.query({
      query: () => ({ url: 'mail-subscriptions' }),
    }),
    getContactTypes: builder.query({
      query: () => ({ url: 'customer/contacts/types' }),
    }),
    getContactLevels: builder.query({
      query: () => ({ url: 'customer/contacts/levels' }),
    }),
  }),
});
